import * as React from "react";
import { useState } from "react";

import {
  PopoverRoot,
  PopoverTrigger,
} from "@replo/design-system/components/popover/Popover";
import {
  SidebarMenu,
  SidebarMenuItem,
} from "@replo/design-system/components/sidebar/Sidebar";
import { skipToken } from "@tanstack/react-query";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { AddWorkspaceModal } from "@v2/features/dashboard/sidebar/project-selector/AddWorkspaceModal";
import {
  ProjectSelectorHeader,
  ProjectSelectorHeaderSkeleton,
} from "@v2/features/dashboard/sidebar/project-selector/ProjectSelectorHeader";
import ProjectSelectorMenuContent from "@v2/features/dashboard/sidebar/project-selector/ProjectSelectorMenuContent";
import { CreateShopModal } from "@v2/features/new-shop-onboarding/CreateShopModal";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { projectRouter } from "@v2/trpc/routes/project";

export function ProjectSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateShopModalOpen, setIsCreateShopModalOpen] = useState(false);
  const [isAddWorkspaceModalOpen, setIsAddWorkspaceModalOpen] = useState(false);

  const workspaceId = useCurrentWorkspaceId();
  const { project: currentProject } = useCurrentProjectContext();
  const { user } = useCurrentUser();

  // NOTE (Ben, 2026-02-24): We query listGroupedByWorkspaceForUser here (auth: user)
  // instead of relying on useCurrentWorkspace() (auth: workspace) because project-only
  // members don't have workspace membership and the workspace query would
  // return FORBIDDEN.
  const { data: workspaces, isPending: isWorkspacesLoading } =
    projectRouter.listGroupedByWorkspaceForUser.useQuery(
      user?.id
        ? {
            userId: user.id,
            currentWorkspaceId: workspaceId ?? undefined,
          }
        : skipToken,
    );

  const currentWorkspace = React.useMemo(() => {
    return workspaces?.find(
      (workspace) => workspace.workspaceId === workspaceId,
    );
  }, [workspaces, workspaceId]);

  const isLoading = isWorkspacesLoading || !workspaceId;

  if (!currentWorkspace && !isLoading) {
    return null;
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <PopoverRoot open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger
              disabled={isLoading}
              className="w-full"
              UNSAFE_className="pb-2"
              data-testid="select-workspace"
            >
              {isLoading || !currentWorkspace ? (
                <ProjectSelectorHeaderSkeleton />
              ) : (
                <ProjectSelectorHeader
                  currentProject={currentProject}
                  workspaceName={currentWorkspace.workspaceName}
                />
              )}
            </PopoverTrigger>
            {workspaceId && (
              <ProjectSelectorMenuContent
                currentProject={currentProject}
                currentWorkspaceId={workspaceId}
                workspaces={workspaces}
                setIsCreateShopModalOpen={setIsCreateShopModalOpen}
                setIsAddWorkspaceModalOpen={setIsAddWorkspaceModalOpen}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
              />
            )}
          </PopoverRoot>
        </SidebarMenuItem>
      </SidebarMenu>

      <AddWorkspaceModal
        open={isAddWorkspaceModalOpen}
        onOpenChange={setIsAddWorkspaceModalOpen}
      />
      <CreateShopModal
        open={isCreateShopModalOpen}
        onOpenChange={setIsCreateShopModalOpen}
        workspaceId={workspaceId ?? undefined}
      />
    </>
  );
}
